import React, { useEffect, useState } from "react";
import { Card, Typography, Input, Button, Alert, Space } from "antd";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import api from "../api";

const { Title, Text, Paragraph } = Typography;

const MFASetup: React.FC = () => {
    const [qrUrl, setQrUrl] = useState<string | null>(null);
    const [secret, setSecret] = useState<string | null>(null);
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState("");
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchSetup = async () => {
            setInitialLoading(true);
            setError("");

            try {
                const user = JSON.parse(
                    localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
                );

                if (user?.mfa_enabled) {
                    if (user.account_type === "ccdaia") {
                        navigate("/cc/registrations", { replace: true });
                    } else if (user.account_type === "dolphin") {
                        navigate("/dolphin/rates", { replace: true });
                    } else {
                        navigate("/", { replace: true });
                    }
                    return;
                }

                const res = await api.post("/users/mfa/setup/start/");
                setQrUrl(res.data.otpauth_url);
                setSecret(res.data.secret);
            } catch (err: any) {
                setError(err.response?.data?.error || "Failed to start MFA setup.");
            } finally {
                setInitialLoading(false);
            }
        };

        fetchSetup();
    }, [navigate]);

    const handleConfirm = async () => {
        setLoading(true);
        setError("");

        try {
            const res = await api.post("/users/mfa/setup/confirm/", {
                code,
            });

            const codes = res.data.backup_codes || [];
            setBackupCodes(codes);

            const userRes = await api.get("/auth/users/me/");
            const updatedUser = userRes.data;

            if (localStorage.getItem("access")) {
                localStorage.setItem("user", JSON.stringify(updatedUser));
            } else {
                sessionStorage.setItem("user", JSON.stringify(updatedUser));
            }
        } catch (err: any) {
            setError(err.response?.data?.error || "Invalid code");
        } finally {
            setLoading(false);
        }
    };

    const handleContinue = () => {
        const user = JSON.parse(
            localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
        );

        if (user.account_type === "ccdaia") {
            navigate("/cc/registrations", { replace: true });
        } else if (user.account_type === "dolphin") {
            navigate("/dolphin/rates", { replace: true });
        } else {
            navigate("/", { replace: true });
        }
    };

    if (backupCodes.length > 0) {
        return (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 100 }}>
                <Card style={{ width: 480 }}>
                    <Title level={3}>MFA Enabled</Title>

                    <Alert
                        type="success"
                        message="Multi-factor authentication has been enabled successfully."
                        showIcon
                        style={{ marginBottom: 20 }}
                    />

                    <Paragraph>
                        Please save these backup codes in a safe place. Each code can only be used once
                        if you lose access to your authenticator app.
                    </Paragraph>

                    <Card size="small" style={{ background: "#fafafa", marginBottom: 20 }}>
                        <Space direction="vertical" style={{ width: "100%" }}>
                            {backupCodes.map((backupCode) => (
                                <Text code key={backupCode}>
                                    {backupCode}
                                </Text>
                            ))}
                        </Space>
                    </Card>

                    <Button type="primary" block onClick={handleContinue}>
                        I Have Saved My Backup Codes
                    </Button>
                </Card>
            </div>
        );
    }

    return (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 100 }}>
            <Card style={{ width: 420 }}>
                <Title level={3}>Set up MFA</Title>

                <Paragraph>
                    Scan this QR code with Google Authenticator, Microsoft Authenticator, or
                    1Password.
                </Paragraph>

                {initialLoading ? (
                    <Text>Loading QR code...</Text>
                ) : qrUrl ? (
                    <div style={{ textAlign: "center", margin: 20 }}>
                        <QRCodeCanvas value={qrUrl} size={200} />
                    </div>
                ) : null}

                <Text type="secondary">Or enter this key manually:</Text>

                <div style={{ marginBottom: 20, marginTop: 8 }}>
                    <Text code>{secret || "Loading..."}</Text>
                </div>

                <Input
                    placeholder="Enter 6-digit code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    maxLength={6}
                    inputMode="numeric"
                />

                {error && <Alert type="error" message={error} style={{ marginTop: 10 }} />}

                <Button
                    type="primary"
                    block
                    style={{ marginTop: 20 }}
                    onClick={handleConfirm}
                    loading={loading}
                    disabled={code.length !== 6 || initialLoading}
                >
                    Confirm
                </Button>
            </Card>
        </div>
    );
};

export default MFASetup;